import {
  IsOptional,
  IsInt,
  Min,
  IsString,
  IsDateString,
} from 'class-validator';
import { ApiPropertyOptional } from '@nestjs/swagger';
import { Type } from 'class-transformer';

/**
 * DTO para filtrar la búsqueda de Órdenes de Transporte.
 * Permite filtrar por cliente, proveedor, nave, temporada, fechas, etc.
 *
 * @example
 * ```typescript
 * // Filtrar por cliente y temporada
 * const filters: OrdenesTransporteFilterDto = {
 *   clienteId: 66,
 *   temporadaId: 7,
 *   page: 1,
 *   limit: 10
 * };
 * ```
 */
export class OrdenesTransporteFilterDto {
  @ApiPropertyOptional({
    description: 'ID del cliente',
    example: 66,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  clienteId?: number;

  @ApiPropertyOptional({
    description: 'ID del exportador',
    example: 66,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  exportadorId?: number;

  @ApiPropertyOptional({
    description: 'Nombre de la nave',
    example: 'MSC SANTA MARIA',
  })
  @IsOptional()
  @IsString()
  nave?: string;

  @ApiPropertyOptional({
    description: 'ID de la temporada',
    example: 7,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  temporadaId?: number;

  @ApiPropertyOptional({
    description: 'Fecha de inicio del rango (YYYY-MM-DD)',
    example: '2025-01-01',
  })
  @IsOptional()
  @IsDateString()
  dateFrom?: string;

  @ApiPropertyOptional({
    description: 'Fecha de fin del rango (YYYY-MM-DD)',
    example: '2025-12-31',
  })
  @IsOptional()
  @IsDateString()
  dateTo?: string;

  @ApiPropertyOptional({
    description: 'Referencia del cliente',
    example: 'REF-001',
  })
  @IsOptional()
  @IsString()
  referenciaCliente?: string;

  @ApiPropertyOptional({
    description: 'Número de contenedor',
    example: 'MNBU3399644',
  })
  @IsOptional()
  @IsString()
  contenedor?: string;

  @ApiPropertyOptional({
    description: 'Número de DUS',
    example: '13040817-6',
  })
  @IsOptional()
  @IsString()
  dus?: string;

  @ApiPropertyOptional({
    description: 'Patente del camión (búsqueda parcial)',
    example: 'CLPB80',
  })
  @IsOptional()
  @IsString()
  licensePlate?: string;

  @ApiPropertyOptional({
    description: 'Número de liquidación',
    example: 381,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  nroLiquidacion?: number;

  @ApiPropertyOptional({
    description: 'Número de booking',
    example: '070ISA1205214',
  })
  @IsOptional()
  @IsString()
  booking?: string;

  @ApiPropertyOptional({
    description: 'Número de página',
    type: Number,
    example: 1,
    default: 1,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  page?: number = 1;

  @ApiPropertyOptional({
    description: 'Elementos por página',
    type: Number,
    example: 10,
    default: 10,
  })
  @IsOptional()
  @Type(() => Number)
  @IsInt()
  @Min(1)
  limit?: number = 10;

  @ApiPropertyOptional({
    description: "Columna para ordenar (ej: 'etd', 'cliente', 'nroOt')",
    example: 'etd',
    default: 'etd',
  })
  @IsOptional()
  @IsString()
  orderBy?: string = 'etd';

  @ApiPropertyOptional({
    description: "Dirección de orden: 'ASC' o 'DESC'",
    enum: ['ASC', 'DESC'],
    example: 'DESC',
    default: 'DESC',
  })
  @IsOptional()
  @IsString()
  orderDirection?: 'ASC' | 'DESC' = 'DESC';
}
